// ═══════════════════════════════════════════════════════════════════════════
// El estado de la comisión, con las palabras del operador
//
// "Las remisiones generadas están en pendientes, pero ACREDITADAS en la vista
//  del vendedor. Las pagadas por el cliente están POR ACREDITARSE y las
//  LIQUIDADAS ya se le depositaron."
//
// Son tres momentos distintos y el vendedor tiene que poder distinguirlos de
// un vistazo, en cualquier remisión:
//
//   acreditada       la venta ya es suya. El cliente todavía no paga, así que
//                    aún no hay con qué depositarle — pero la venta cuenta.
//   por acreditarse  el cliente ya le pagó a la vinícola. Ahora sí se le debe.
//   liquidada        ya se le depositó.
//
// LO QUE ESTA DISTINCIÓN EVITA: el reclamo de "vendí y no me han pagado"
// cuando lo que pasa es que el cliente no ha pagado. Con un solo número esa
// conversación se repite cada mes; con tres, el vendedor ve dónde está atorado
// cada peso y a quién le toca moverlo.
//
// Va aparte de `cobranza.js` a propósito: aquello es el estado del cobro AL
// CLIENTE y esto el de la comisión AL VENDEDOR. Se parecen y no son lo mismo —
// una remisión cobrada al cliente puede seguir sin liquidarse al vendedor.
// ═══════════════════════════════════════════════════════════════════════════

export const COMISION = {
  sin_comision: {
    clave: "sin_comision", etiqueta: "Sin comisión", color: "#555",
    ayuda: "Esta remisión no genera comisión.",
  },
  acreditada: {
    clave: "acreditada", etiqueta: "Acreditada", color: "#8a8a6a",
    ayuda: "La venta ya es tuya. El cliente todavía no le paga a la vinícola.",
  },
  por_acreditarse: {
    clave: "por_acreditarse", etiqueta: "Por acreditarse", color: "#a8d060",
    ayuda: "El cliente ya pagó. Tu comisión está pendiente de depósito.",
  },
  liquidada: {
    clave: "liquidada", etiqueta: "Liquidada", color: "#4a7a30",
    ayuda: "Ya se te depositó.",
  },
};

/**
 * @param inv remisión con los campos que trae la lectura de Postgres:
 *            comision, comisionExigible, comisionLiquidada
 */
export function estadoComision(inv) {
  // Cancelada no genera nada, aunque tuviera tasa: el documento murió.
  if (!inv || inv.canceledAt) return COMISION.sin_comision;
  if (!inv.comision || Number(inv.comision) <= 0) return COMISION.sin_comision;
  if (inv.comisionLiquidada) return COMISION.liquidada;
  if (inv.comisionExigible) return COMISION.por_acreditarse;
  return COMISION.acreditada;
}

/**
 * El estado de un renglón de la vista `comision_remision`.
 *
 * Va aparte de `estadoComision` porque la vista trae otros nombres
 * (`exigible`, `pagada_al_vendedor`) y ya filtró en la base lo que no genera
 * comisión: canceladas, sin agente y con tasa en cero. Por eso aquí no se
 * repite ese descarte — todo renglón que llega es uno de los tres estados.
 */
export function estadoDeRenglon(r) {
  if (!r) return COMISION.sin_comision;
  if (r.pagada_al_vendedor) return COMISION.liquidada;
  if (r.exigible) return COMISION.por_acreditarse;
  return COMISION.acreditada;
}

/**
 * Pone primero las remisiones del estado que se pidió, SIN sacar ninguna.
 *
 * Ordenar y no filtrar es a propósito: el vendedor que toca "por acreditarse"
 * quiere ver ese dinero junto, no perder de vista el resto — y una lista que
 * se encoge al tocarla es justo la sorpresa que estamos quitando.
 *
 * El orden dentro de cada grupo se respeta (`sort` es estable desde ES2019),
 * así que las remisiones siguen llegando de la más reciente a la más vieja,
 * que es como las manda la consulta.
 *
 * @param primero clave de COMISION, o nada para dejar el orden natural
 */
export function ordenarPorEstado(renglones = [], primero = null) {
  const lista = [...renglones];
  if (!primero || !(primero in COMISION)) return lista;
  return lista.sort((a, b) =>
    (estadoDeRenglon(a).clave === primero ? 0 : 1) -
    (estadoDeRenglon(b).clave === primero ? 0 : 1));
}

/** Los tres totales de una lista de remisiones, con las mismas palabras. */
export function totalesComision(invoices = []) {
  const t = { acreditada: 0, por_acreditarse: 0, liquidada: 0 };
  for (const inv of invoices) {
    const e = estadoComision(inv);
    if (e.clave in t) t[e.clave] += Number(inv.comision) || 0;
  }
  // Se redondea al final y no por renglón: sumar centavos redondeados
  // desviaría el total, que es justo lo que el vendedor va a comparar contra
  // su depósito.
  for (const k of Object.keys(t)) t[k] = Math.round(t[k] * 100) / 100;
  return t;
}
